import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Card, Stack, Popper, MenuItem, Typography } from '@mui/material';

import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar/Scrollbar';

import { ALL_TICKET_TABLE_HEAD } from '../tableHeads';

FilterPopover.propTypes = {
  setFilterBy: PropTypes.func,
  setFilterTitle: PropTypes.func,
  setSearchBy: PropTypes.func,
  setPage: PropTypes.func,
  departmentData: PropTypes.array,
  handleCloseFilter: PropTypes.func,
  filterPopover: PropTypes.object,
  statusesList: PropTypes.array,
  setSubFilterPopover: PropTypes.func,
  subFilterPopover: PropTypes.object,
  handleCloseSubFilter: PropTypes.func,
  tagsData: PropTypes.array,
};

export default function FilterPopover({
  setSubFilterPopover,
  subFilterPopover,
  handleCloseSubFilter,
  filterPopover,
  handleCloseFilter,
  setPage,
  setFilterTitle,
  setFilterBy,
  setSearchBy,
  departmentData,
  statusesList,
  tagsData,
}) {
  const [selectedFilter, setSelectedFilter] = useState('id');
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const handleOpenSubFilter = (event, category) => {
    if (category === 'department') {
      setSubFilterPopover({ department: event.currentTarget });
    } else if (category === 'status') {
      setSubFilterPopover({ status: event.currentTarget });
    } else if (category === 'priority') {
      setSubFilterPopover({ priority: event.currentTarget });
    } else if (category === 'tag') {
      setSubFilterPopover({ tag: event.currentTarget });
    }
  };

  return (
    <>
      <Popper
        open={Boolean(filterPopover)}
        anchorEl={filterPopover}
        placement={isSmallScreen ? 'bottom' : 'left-start'}
        onClose={handleCloseFilter}
        sx={{
          pl: isSmallScreen && 10,
        }}
      >
        <Card
          sx={{
            p: 1,
            minwidth: isSmallScreen ? 110 : 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          {ALL_TICKET_TABLE_HEAD.filter(({ id }) => id !== 'options').map(
            ({ id, name }) =>
              name === 'status' ? (
                <MenuItem
                  key={id}
                  selected={selectedFilter === 'status'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'status')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'status')}
                  onClick={(e) => handleOpenSubFilter(e, 'status')}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('filters.byStatus')}
                    </Typography>
                  </Stack>
                </MenuItem>
              ) : name === 'department' ? (
                <MenuItem
                  key={id}
                  selected={selectedFilter === 'department'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'department')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'department')}
                  onClick={(e) => handleOpenSubFilter(e, 'department')}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('filters.byDepartment')}
                    </Typography>
                  </Stack>
                </MenuItem>
              ) : name === 'priority' ? (
                <MenuItem
                  key={id}
                  selected={selectedFilter === 'priority'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'priority')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'priority')}
                  onClick={(e) => handleOpenSubFilter(e, 'priority')}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('filters.byPriority')}
                    </Typography>
                  </Stack>
                </MenuItem>
              ) : name === 'tag' ? (
                <MenuItem
                  key={id}
                  selected={selectedFilter === 'tag'}
                  onMouseEnter={(e) => handleOpenSubFilter(e, 'tag')}
                  onTouchStart={(e) => handleOpenSubFilter(e, 'tag')}
                  onClick={(e) => handleOpenSubFilter(e, 'tag')}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify
                      icon="eva:arrow-ios-back-outline"
                      sx={{
                        mr: isSmallScreen ? 1.5 : 2,
                        width: isSmallScreen ? 16 : 20,
                        height: isSmallScreen ? 16 : 20,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t('filters.byTag')}
                    </Typography>
                  </Stack>
                </MenuItem>
              ) : ['createdAt', 'updatedAt'].includes(name) ? null : (
                <MenuItem
                  key={id}
                  selected={selectedFilter === name}
                  onClick={() => {
                    setFilterTitle('');
                    setFilterBy(name);
                    setSearchBy(`by${name.charAt(0).toUpperCase() + name.slice(1)}`);
                    setSelectedFilter(name);
                    handleCloseFilter();
                    handleCloseSubFilter();
                  }}
                  onTouchStart={() => {
                    setFilterTitle('');
                    setFilterBy(name);
                    setSearchBy(`by${name.charAt(0).toUpperCase() + name.slice(1)}`);
                    setSelectedFilter(name);
                    handleCloseFilter();
                    handleCloseSubFilter();
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: isSmallScreen && '0.7rem',
                    }}
                  >
                    {t(
                      `filters.by${name && name.charAt(0).toUpperCase() + name.slice(1)}`
                    )}
                  </Typography>
                </MenuItem>
              )
          )}
        </Card>
      </Popper>

      <Popper
        open={Boolean(subFilterPopover.department)}
        anchorEl={subFilterPopover.department}
        placement="left-start"
        onClose={handleCloseSubFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {departmentData.map((department) => (
              <MenuItem
                key={department.id}
                onClick={() => {
                  setFilterBy('department');
                  setSearchBy('byDepartment');
                  setSelectedFilter('department');
                  setFilterTitle(department.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
                onTouchStart={() => {
                  setFilterBy('department');
                  setSearchBy('byDepartment');
                  setSelectedFilter('department');
                  setFilterTitle(department.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {department.name}
                </Typography>
              </MenuItem>
            ))}
          </Scrollbar>
        </Card>
      </Popper>

      <Popper
        open={Boolean(subFilterPopover.status)}
        anchorEl={subFilterPopover.status}
        onClose={handleCloseSubFilter}
        placement="left-start"
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {statusesList.map((status, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  setFilterBy('status');
                  setSearchBy('byStatus');
                  setSelectedFilter('status');
                  setFilterTitle(
                    status.statusId < 6
                      ? status.name
                      : `${status.name}|${status.department}`
                  );
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
                onTouchStart={() => {
                  setFilterBy('status');
                  setSearchBy('byStatus');
                  setSelectedFilter('status');
                  setFilterTitle(
                    status.statusId < 6
                      ? status.name
                      : `${status.name}|${status.department}`
                  );
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
              >
                {status.statusId <= 5 ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {t(`status.${status.name}`)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: isSmallScreen ? '0.55rem' : '0.75rem',
                      }}
                    >
                      - Global
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      sx={{
                        fontSize: isSmallScreen && '0.7rem',
                      }}
                    >
                      {status.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: isSmallScreen ? '0.55rem' : '0.75rem',
                      }}
                    >
                      - {status.department}
                    </Typography>
                  </>
                )}
              </MenuItem>
            ))}
          </Scrollbar>
        </Card>
      </Popper>

      <Popper
        open={Boolean(subFilterPopover.priority)}
        anchorEl={subFilterPopover.priority}
        placement="left-start"
        onClose={handleCloseSubFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
              boxShadow: '0.5px 0.5px 2 px',
            },
          }}
        >
          {[
            { value: 'LOW', display: t('priority.LOW') },
            { value: 'MEDIUM', display: t('priority.MEDIUM') },
            { value: 'HIGH', display: t('priority.HIGH') },
          ].map((priority, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                setFilterBy('priority');
                setSearchBy('byPriority');
                setSelectedFilter('priority');
                setFilterTitle(priority.value);
                handleCloseFilter();
                handleCloseSubFilter();
                setPage(0);
              }}
              onTouchStart={() => {
                setFilterBy('priority');
                setSearchBy('byPriority');
                setSelectedFilter('priority');
                setFilterTitle(priority.value);
                handleCloseFilter();
                handleCloseSubFilter();
                setPage(0);
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: isSmallScreen && '0.7rem',
                }}
              >
                {priority.display}
              </Typography>
            </MenuItem>
          ))}
        </Card>
      </Popper>

      <Popper
        open={Boolean(subFilterPopover.tag)}
        anchorEl={subFilterPopover.tag}
        placement="left-start"
        onClose={handleCloseSubFilter}
      >
        <Card
          sx={{
            p: 1,
            minwidth: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          }}
        >
          <Scrollbar sx={{ maxHeight: 200, overflowY: 'auto' }}>
            {tagsData.map((tag) => (
              <MenuItem
                key={tag.id}
                onClick={() => {
                  setFilterBy('tag');
                  setSearchBy('byTag');
                  setSelectedFilter('tag');
                  setFilterTitle(tag.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
                onTouchStart={() => {
                  setFilterBy('tag');
                  setSearchBy('byTag');
                  setSelectedFilter('tag');
                  setFilterTitle(tag.name);
                  handleCloseFilter();
                  handleCloseSubFilter();
                  setPage(0);
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isSmallScreen && '0.7rem',
                  }}
                >
                  {tag.name}
                </Typography>
              </MenuItem>
            ))}
          </Scrollbar>
        </Card>
      </Popper>
    </>
  );
}
